import { useState, useEffect, useCallback } from 'react'
import { conversationsApi } from '@/services/conversationsApi'

interface CallIntent {
  type?: string
  confidence?: number
  entities?: Record<string, unknown>
  [key: string]: unknown
}

export interface PhoneCall {
  id: string
  call_type: 'telnyx' | 'elevenlabs' | 'hybrid' | 'unknown'
  external_id: string
  caller: {
    id: string
    phone_number: string
    name: string
    is_verified: boolean
    tags?: string[]
  }
  agent: {
    id: string
    agent_id: string
    name: string
    is_active: boolean
  }
  status: 'initiated' | 'ringing' | 'answered' | 'bridged' | 'completed' | 'failed' | 'hangup'
  duration_seconds: number | null
  cost: number | null
  summary: string | null
  intent: CallIntent | null
  successful: boolean | null
  turns_count: number
  started_at: string
  answered_at: string | null
  ended_at: string | null
  feedback: string
  archived: boolean
}

export interface PhoneCallsResponse {
  calls: PhoneCall[]
  pagination: {
    page: number
    limit: number
    total_pages: number
    total_count: number
    has_next: boolean
    has_previous: boolean
  }
}

export interface PhoneCallsFilters {
  type?: 'telnyx' | 'elevenlabs' | 'all'
  status?: string
  caller_phone?: string
  agent_id?: string
  tags?: string
  tag_ids?: string
  category?: string
}

const DEFAULT_PAGINATION: PhoneCallsResponse['pagination'] = {
  page: 1,
  limit: 20,
  total_pages: 0,
  total_count: 0,
  has_next: false,
  has_previous: false,
}

export function usePhoneCalls() {
  const [calls, setCalls] = useState<PhoneCall[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
  const [filters, setFilters] = useState<PhoneCallsFilters>({})

  const fetchCalls = useCallback(async (page = 1, newFilters: PhoneCallsFilters = {}) => {
    // Track loadingMore from the page being requested, not pagination.page —
    // that only updates once the request resolves, so deriving it from the
    // stale value made the very first "next page" fetch (1 -> 2) look like an
    // initial load and trigger the full-page skeleton instead of the inline loader.
    if (page > 1) {
      setLoadingMore(true)
    } else {
      setLoading(true)
    }
    setError(null)

    try {
      const safeLimit = pagination?.limit ?? DEFAULT_PAGINATION.limit
      const params = new URLSearchParams({
        page: page.toString(),
        limit: safeLimit.toString(),
        ...newFilters,
      })

      const response = await conversationsApi.get(`/api/v1/phone-calls/calls/?${params.toString()}`) as Partial<PhoneCallsResponse> | PhoneCall[]
      const callsList = Array.isArray(response)
        ? response
        : Array.isArray(response?.calls)
          ? response.calls
          : []
      const nextPagination =
        !Array.isArray(response) && response?.pagination
          ? { ...DEFAULT_PAGINATION, ...response.pagination, page }
          : { ...DEFAULT_PAGINATION, page, limit: safeLimit, total_count: callsList.length }

      if (page === 1) {
        setCalls(callsList)
      } else {
        setCalls(prev => [...prev, ...callsList])
      }

      setPagination(nextPagination)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error loading phone calls'
      setError(errorMessage)
      // Keep previous stable pagination shape to avoid runtime crashes.
      setPagination((prev) => ({ ...DEFAULT_PAGINATION, ...prev }))
      console.error('Error fetching phone calls:', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [pagination.limit])

  const loadMore = useCallback(() => {
    if (pagination.has_next && !loading && !loadingMore) {
      fetchCalls(pagination.page + 1, filters)
    }
  }, [fetchCalls, pagination.has_next, pagination.page, loading, loadingMore, filters])

  const refetch = useCallback(() => {
    fetchCalls(1, filters)
  }, [fetchCalls, filters])

  const updateFilters = useCallback((newFilters: PhoneCallsFilters) => {
    setFilters(newFilters)
    fetchCalls(1, newFilters)
  }, [fetchCalls])

  // Load initial data
  useEffect(() => {
    fetchCalls(1, filters)
  }, [fetchCalls, filters])

  return {
    calls,
    loading,
    error,
    pagination,
    filters,
    hasMore: pagination.has_next,
    loadingMore,
    refetch,
    loadMore,
    updateFilters,
    setFilters,
  }
}
